import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Trash2, AlertTriangle } from "lucide-react";
import { api, STATUS_LABELS } from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [applications, setApplications] = useState([]);
  const [gearOrders, setGearOrders] = useState([]);
  const [assignFor, setAssignFor] = useState(null);
  const [helperPick, setHelperPick] = useState("");
  const [reviewApp, setReviewApp] = useState(null);
  const [decisionReason, setDecisionReason] = useState("");
  const [intSchedule, setIntSchedule] = useState("");
  const [intNotes, setIntNotes] = useState("");
  const [wipeOpen, setWipeOpen] = useState(false);
  const [wipeMode, setWipeMode] = useState("safe");
  const [wipeConfirm, setWipeConfirm] = useState("");
  const [wiping, setWiping] = useState(false);
  const [profileUser, setProfileUser] = useState(null);
  const [profileTasks, setProfileTasks] = useState([]);
  const [profileApp, setProfileApp] = useState(null);

  const openProfile = async (u) => {
    setProfileUser(u);
    setProfileTasks([]);
    setProfileApp(null);
    try {
      // Tasks of this user
      const role = u.role === "helper" ? "helper" : "client";
      const res = await api.get(`/admin/users/${u.id}/tasks`);
      setProfileTasks(res.data || []);
      // Application if helper
      if (u.role === "helper") {
        const app = applications.find((a) => a.helper_id === u.id);
        if (app) setProfileApp(app);
      }
    } catch {/* ignore — endpoint may not exist; fallback to applications list */}
  };

  const load = async () => {
    const [s, u, t, a, g] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/users"), api.get("/admin/tasks"),
      api.get("/admin/applications"), api.get("/admin/gear-orders"),
    ]);
    setStats(s.data); setUsers(u.data); setTasks(t.data); setApplications(a.data); setGearOrders(g.data);
  };
  useEffect(() => { load(); }, []);

  const pendingApps = applications.filter((a) => a.status === "pending");
  const helpers = users.filter((u) => u.role === "helper");

  const suspend = async (id, suspended) => {
    await api.post(`/admin/users/${id}/${suspended ? "unsuspend" : "suspend"}`);
    toast.success("Mis à jour"); load();
  };
  const cancel = async (id) => { await api.post(`/tasks/${id}/cancel`); toast.success("Annulée"); load(); };

  const deleteUser = async (id, name) => {
    if (!window.confirm(`Supprimer définitivement ${name} ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Utilisateur supprimé");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const deleteTask = async (id, title) => {
    if (!window.confirm(`Supprimer la tâche "${title}" ? Tous ses messages, soumissions et paiements seront effacés.`)) return;
    try {
      await api.delete(`/admin/tasks/${id}`);
      toast.success("Tâche supprimée");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const deleteApplication = async (id, name) => {
    if (!window.confirm(`Supprimer la candidature de ${name} ?`)) return;
    try {
      await api.delete(`/admin/applications/${id}`);
      toast.success("Candidature supprimée");
      setReviewApp(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const approveHelperDirect = async (u) => {
    if (!window.confirm(`Approuver directement ${u.full_name} comme helper actif ?\n\nCela contourne la candidature formelle (entrevue + photos).`)) return;
    try {
      await api.post(`/admin/users/${u.id}/approve-helper`);
      toast.success("Helper approuvé !");
      setProfileUser(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const revokeHelper = async (u) => {
    if (!window.confirm(`Retirer l'approbation de ${u.full_name} ?\nIl ne pourra plus accepter de tâches.`)) return;
    try {
      await api.post(`/admin/users/${u.id}/revoke-helper`);
      toast.success("Approbation retirée");
      setProfileUser(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const wipeAll = async () => {
    if (wipeConfirm.trim().toUpperCase() !== "EFFACER") {
      toast.error("Tape EFFACER pour confirmer");
      return;
    }
    setWiping(true);
    try {
      const { data } = await api.post("/admin/wipe", { mode: wipeMode });
      toast.success(`Base nettoyée — ${Object.values(data.counts).reduce((a, b) => a + b, 0)} éléments supprimés`);
      setWipeOpen(false);
      setWipeConfirm("");
      setWipeMode("safe");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setWiping(false); }
  };
  const assign = async () => {
    if (!helperPick) return;
    await api.post(`/admin/tasks/${assignFor}/assign/${helperPick}`);
    toast.success("Assignée"); setAssignFor(null); setHelperPick(""); load();
  };
  const decide = async (action) => {
    try {
      await api.post(`/admin/applications/${reviewApp.id}/${action}`, { reason: decisionReason });
      toast.success(action === "approve" ? "Approuvée" : "Refusée");
      setReviewApp(null); setDecisionReason(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const saveInterview = async (payload) => {
    try {
      await api.post(`/admin/applications/${reviewApp.id}/interview`, payload);
      toast.success("Entrevue mise à jour");
      const fresh = (await api.get("/admin/applications")).data;
      setApplications(fresh);
      setReviewApp(fresh.find((a) => a.id === reviewApp.id) || null);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-3xl md:text-4xl font-heading font-black text-navy">Tableau admin</h1>
        <button
          onClick={() => { setWipeConfirm(""); setWipeMode("safe"); setWipeOpen(true); }}
          className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-full px-5 py-2.5 text-sm transition-colors"
          data-testid="admin-wipe-btn"
        >
          <Trash2 size={16} /> Tout effacer
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat l="Utilisateurs" v={stats.users} />
          <Stat l="Clients" v={stats.clients} />
          <Stat l="Helpers" v={stats.helpers} />
          <Stat l="Tâches" v={stats.tasks_total} />
          <Stat l="Publiées" v={stats.tasks_published} />
          <Stat l="Complétées" v={stats.tasks_completed} />
          <Stat l="Litiges" v={stats.tasks_disputes} />
          <Stat l="Paiements" v={stats.payments_paid} />
        </div>
      )}

      <Tabs defaultValue="tasks">
        <TabsList>
          <TabsTrigger value="tasks" data-testid="admin-tab-tasks">Tâches</TabsTrigger>
          <TabsTrigger value="applications" data-testid="admin-tab-applications">
            Candidatures{pendingApps.length > 0 && <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-lime text-navy text-xs font-black">{pendingApps.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Utilisateurs</TabsTrigger>
          <TabsTrigger value="gear" data-testid="admin-tab-gear">
            Équipement{gearOrders.filter((g) => g.status === "pending").length > 0 && <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-lime text-navy text-xs font-black">{gearOrders.filter((g) => g.status === "pending").length}</span>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tasks" className="space-y-2 mt-4">
          {tasks.map((t) => {
            const s = STATUS_LABELS[t.status];
            return (
              <div key={t.id} className="card-surface p-4 flex items-center gap-4 flex-wrap" data-testid={`admin-task-${t.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`chip ${s.color}`}>{s.fr}</span>
                    {t.urgent && <span className="chip-lime">Urgent</span>}
                  </div>
                  <Link to={`/tasks/${t.id}`} className="font-bold text-navy hover:underline block mt-1 truncate">{t.title}</Link>
                  <div className="text-xs text-[#475569]">{t.client_name} · {t.helper_name || "—"} · {t.area}</div>
                </div>
                <div className="font-heading font-black text-navy text-lg">{t.total.toFixed(2)} $</div>
                <div className="flex gap-2">
                  {!t.helper_id && t.status === "published" && (
                    <button onClick={() => setAssignFor(t.id)} className="btn-outline py-1.5 px-3 text-xs" data-testid={`admin-assign-${t.id}`}>Assigner</button>
                  )}
                  {!["cancelled", "paid_out"].includes(t.status) && (
                    <button onClick={() => cancel(t.id)} className="btn-outline py-1.5 px-3 text-xs text-red-600 border-red-300" data-testid={`admin-cancel-${t.id}`}>Annuler</button>
                  )}
                  <button onClick={() => deleteTask(t.id, t.title)} className="inline-flex items-center gap-1 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-full py-1.5 px-3 text-xs font-bold" data-testid={`admin-del-task-${t.id}`}>
                    <Trash2 size={12} /> Suppr.
                  </button>
                </div>
              </div>
            );
          })}
        </TabsContent>

        <TabsContent value="applications" className="space-y-2 mt-4">
          {applications.length === 0 && (
            <div className="card-surface p-6 text-center text-[#475569]" data-testid="admin-no-apps">
              Aucune candidature pour le moment.
            </div>
          )}
          {applications.map((a) => {
            const statusLabel = a.status === "pending" ? "En attente" : a.status === "approved" ? "Approuvée" : "Refusée";
            const statusColor = a.status === "pending" ? "bg-amber-100 text-amber-800" : a.status === "approved" ? "bg-lime text-navy" : "bg-red-100 text-red-800";
            const photosOk = a.id_photo_path && a.profile_photo_path;
            return (
              <div
                key={a.id}
                className="card-surface p-4 flex items-center gap-4 w-full hover:ring-2 hover:ring-navy/20 transition-all"
                data-testid={`admin-app-${a.id}`}
              >
                <button
                  onClick={() => setReviewApp(a)}
                  className="flex items-center gap-4 flex-1 min-w-0 text-left"
                  data-testid={`admin-app-open-${a.id}`}
                >
                  <div className="h-10 w-10 rounded-full bg-navy text-lime font-black flex items-center justify-center shrink-0">
                    {(a.helper_name || "?")[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-navy truncate">{a.helper_name}</span>
                      <span className={`chip ${statusColor}`}>{statusLabel}</span>
                      {a.status === "pending" && (
                        <>
                          {a.interview_completed
                            ? <span className="chip bg-lime/30 text-navy text-[10px]">Entrevue ✓</span>
                            : <span className="chip bg-amber-100 text-amber-800 text-[10px]">Entrevue à faire</span>}
                          {photosOk
                            ? <span className="chip bg-lime/30 text-navy text-[10px]">Photos ✓</span>
                            : <span className="chip bg-amber-100 text-amber-800 text-[10px]">Photos manquantes</span>}
                        </>
                      )}
                    </div>
                    <div className="text-xs text-[#475569] truncate mt-0.5">
                      {a.helper_email} · {a.city || "—"} · {a.phone || "—"}
                    </div>
                  </div>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); deleteApplication(a.id, a.helper_name); }}
                  className="inline-flex items-center gap-1 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-full py-1.5 px-3 text-xs font-bold shrink-0"
                  data-testid={`admin-del-app-row-${a.id}`}
                  title="Supprimer la candidature"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            );
          })}
        </TabsContent>

        <TabsContent value="users" className="space-y-2 mt-4">
          {users.map((u) => (
            <div key={u.id} className="card-surface p-4 flex items-center gap-4" data-testid={`admin-user-${u.id}`}>
              <div className="h-10 w-10 rounded-full bg-navy text-lime font-black flex items-center justify-center">{u.full_name[0]}</div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-navy truncate">{u.full_name} <span className="chip ml-1">{u.role}</span></div>
                <div className="text-sm text-[#475569] truncate">{u.email} · {u.area || "—"}</div>
              </div>
              {u.role !== "admin" && (
                <div className="flex gap-2 flex-wrap">
                  <button onClick={() => openProfile(u)} className="btn-outline py-1.5 px-3 text-xs" data-testid={`admin-view-${u.id}`}>
                    Voir profil
                  </button>
                  <button onClick={() => suspend(u.id, u.suspended)} className={`btn-outline py-1.5 px-3 text-xs ${u.suspended ? "" : "text-amber-700 border-amber-300"}`} data-testid={`admin-suspend-${u.id}`}>
                    {u.suspended ? "Réactiver" : "Suspendre"}
                  </button>
                  <button onClick={() => deleteUser(u.id, u.full_name)} className="inline-flex items-center gap-1 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-full py-1.5 px-3 text-xs font-bold" data-testid={`admin-del-user-${u.id}`}>
                    <Trash2 size={12} /> Suppr.
                  </button>
                </div>
              )}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="gear" className="space-y-2 mt-4">
          {gearOrders.length === 0 && (
            <div className="card-surface p-6 text-center text-[#475569]" data-testid="admin-no-gear">
              Aucune commande d'équipement pour le moment.
            </div>
          )}
          {gearOrders.map((g) => {
            const statusMap = {
              pending: { label: "En attente", color: "bg-amber-100 text-amber-800" },
              approved: { label: "Approuvée", color: "bg-lime/30 text-navy" },
              shipped: { label: "Expédiée", color: "bg-blue-100 text-blue-800" },
              delivered: { label: "Livrée", color: "bg-lime text-navy" },
              cancelled: { label: "Annulée", color: "bg-red-100 text-red-800" },
            };
            const s = statusMap[g.status] || statusMap.pending;
            return (
              <div key={g.id} className="card-surface p-4 flex items-center gap-4 flex-wrap" data-testid={`admin-gear-${g.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`chip ${s.color}`}>{s.label}</span>
                    <span className="font-bold text-navy">{g.item_label}</span>
                    <span className="chip bg-surface text-navy/70 text-[10px]">Taille {g.size}</span>
                  </div>
                  <div className="text-xs text-[#475569] mt-1">
                    <strong>{g.helper_name}</strong> · {g.helper_email}
                  </div>
                  <div className="text-xs text-[#94A3B8] mt-0.5">
                    {g.tasks_completed} tâche{g.tasks_completed > 1 ? "s" : ""} complétée{g.tasks_completed > 1 ? "s" : ""} · {new Date(g.created_at).toLocaleDateString("fr-CA")}
                  </div>
                  {g.notes && <div className="text-xs text-[#475569] mt-1 italic">« {g.notes} »</div>}
                </div>
                <div className="font-heading font-black text-navy text-lg">{(g.price || 0).toFixed(2)} $</div>
                <div className="flex gap-2 flex-wrap">
                  <select
                    value={g.status}
                    onChange={async (e) => {
                      try {
                        await api.post(`/admin/gear-orders/${g.id}/status`, { status: e.target.value });
                        toast.success("Statut mis à jour");
                        load();
                      } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
                    }}
                    className="text-xs rounded-full border border-[#E2E8F0] bg-white px-3 py-1.5 font-bold text-navy"
                    data-testid={`admin-gear-status-${g.id}`}
                  >
                    <option value="pending">En attente</option>
                    <option value="approved">Approuvée</option>
                    <option value="shipped">Expédiée</option>
                    <option value="delivered">Livrée</option>
                    <option value="cancelled">Annulée</option>
                  </select>
                  <button
                    onClick={async () => {
                      if (!window.confirm("Supprimer cette commande ?")) return;
                      try {
                        await api.delete(`/admin/gear-orders/${g.id}`);
                        toast.success("Supprimée");
                        load();
                      } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
                    }}
                    className="inline-flex items-center gap-1 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-full py-1.5 px-3 text-xs font-bold"
                    data-testid={`admin-del-gear-${g.id}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            );
          })}
        </TabsContent>
      </Tabs>

      <Dialog open={!!assignFor} onOpenChange={() => setAssignFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assigner un helper</DialogTitle></DialogHeader>
          <select className="input-base" value={helperPick} onChange={(e) => setHelperPick(e.target.value)} data-testid="helper-select">
            <option value="">Choisir...</option>
            {helpers.map((h) => <option key={h.id} value={h.id}>{h.full_name} · {h.helper_profile?.badge || "Helper"}</option>)}
          </select>
          <DialogFooter><button onClick={assign} className="btn-primary w-full" data-testid="confirm-assign">Assigner</button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reviewApp} onOpenChange={() => { setReviewApp(null); setDecisionReason(""); setIntSchedule(""); setIntNotes(""); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Candidature · {reviewApp?.helper_name}</DialogTitle></DialogHeader>
          {reviewApp && (
            <div className="space-y-3 text-sm">
              {(reviewApp.id_photo_path || reviewApp.profile_photo_path) && (
                <div className="grid grid-cols-2 gap-3">
                  {reviewApp.profile_photo_path && (
                    <PhotoPreview label="Photo profil" path={reviewApp.profile_photo_path} />
                  )}
                  {reviewApp.id_photo_path && (
                    <PhotoPreview label="Pièce d'identité" path={reviewApp.id_photo_path} />
                  )}
                </div>
              )}
              <AppRow l="Email" v={reviewApp.helper_email} />
              <AppRow l="Téléphone" v={reviewApp.phone} />
              <AppRow l="Ville" v={reviewApp.city} />
              <AppRow l="Déplacement" v={reviewApp.willing_to_travel ? `Oui · jusqu'à ${reviewApp.max_km} km` : "Non"} />
              <AppRow l="Disponibilités" v={(reviewApp.availability || []).join(", ")} />
              <AppRow l="Véhicule" v={reviewApp.has_vehicle ? `Oui (${reviewApp.vehicle_type})` : "Non"} />
              <AppRow l="Catégories" v={(reviewApp.task_categories || []).join(", ")} />
              <AppRow l="Expérience" v={reviewApp.has_experience ? `Oui · ${reviewApp.experience_details || ""}` : "Non"} />
              <AppRow l="Pourquoi bon helper" v={reviewApp.why_good_helper} />
              <AppRow l="Parrainé par" v={reviewApp.referred_by_name || "—"} />
              <AppRow l="Conditions" v={reviewApp.accepts_platform_rules ? "✅ Acceptées" : "❌"} />

              <div className="bg-surface rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2 font-bold text-navy">📞 Entrevue téléphonique</div>
                <AppRow l="Statut" v={reviewApp.interview_status || "non planifiée"} />
                {reviewApp.interview_scheduled_at && <AppRow l="Planifiée" v={new Date(reviewApp.interview_scheduled_at).toLocaleString()} />}
                {reviewApp.interview_notes && <AppRow l="Notes" v={reviewApp.interview_notes} />}
                {reviewApp.status === "pending" && !reviewApp.interview_completed && (
                  <div className="space-y-2">
                    <input type="datetime-local" className="input-base" value={intSchedule} onChange={(e) => setIntSchedule(e.target.value)} data-testid="int-schedule" />
                    <textarea rows={2} className="input-base" placeholder="Notes d'entrevue..." value={intNotes} onChange={(e) => setIntNotes(e.target.value)} data-testid="int-notes" />
                    <div className="flex gap-2">
                      <button onClick={() => saveInterview({ scheduled_at: intSchedule, notes: intNotes })} className="btn-outline flex-1 py-2 text-xs" data-testid="schedule-interview">
                        Planifier
                      </button>
                      <button onClick={() => saveInterview({ notes: intNotes, completed: true })} className="btn-primary flex-1 py-2 text-xs" data-testid="complete-interview">
                        Marquer complétée
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {reviewApp.status === "pending" && (
                <>
                  <div>
                    <label className="text-sm font-bold text-navy">Raison (optionnel)</label>
                    <textarea rows={2} className="input-base mt-1" value={decisionReason} onChange={(e) => setDecisionReason(e.target.value)} data-testid="decision-reason" />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => decide("reject")} className="btn-outline flex-1 text-red-600 border-red-300" data-testid="reject-app">Refuser</button>
                    <button onClick={() => decide("approve")} className="btn-primary flex-1" data-testid="approve-app"
                      disabled={!reviewApp.interview_completed || !reviewApp.id_photo_path || !reviewApp.profile_photo_path}
                      title={!reviewApp.interview_completed ? "Entrevue requise" : (!reviewApp.id_photo_path || !reviewApp.profile_photo_path) ? "Photos requises" : ""}>
                      Approuver
                    </button>
                  </div>
                  {(!reviewApp.interview_completed || !reviewApp.id_photo_path || !reviewApp.profile_photo_path) && (
                    <p className="text-xs text-amber-700 italic">
                      ⚠ Approbation impossible tant que : entrevue complétée + 2 photos téléversées.
                    </p>
                  )}
                </>
              )}
              {reviewApp.status !== "pending" && (
                <div className="bg-surface rounded-xl p-3 text-[#475569]">
                  <strong className="text-navy">Décision :</strong> {reviewApp.status} · {reviewApp.admin_reason || "—"}
                </div>
              )}

              <div className="pt-3 border-t border-[#E2E8F0]">
                <button
                  onClick={() => deleteApplication(reviewApp.id, reviewApp.helper_name)}
                  className="w-full inline-flex items-center justify-center gap-2 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-full py-2 text-sm font-bold"
                  data-testid="admin-del-app"
                >
                  <Trash2 size={14} /> Supprimer cette candidature
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!profileUser} onOpenChange={(o) => !o && setProfileUser(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="admin-profile-dialog">
          {profileUser && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span className="h-10 w-10 rounded-full bg-navy text-lime font-black flex items-center justify-center">
                    {profileUser.full_name[0].toUpperCase()}
                  </span>
                  {profileUser.full_name}
                  <span className="chip text-[10px]">{profileUser.role}</span>
                  {profileUser.suspended && <span className="chip bg-red-100 text-red-700 text-[10px]">Suspendu</span>}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-surface rounded-lg p-3">
                    <div className="text-[10px] font-black uppercase text-[#94A3B8]">Email</div>
                    <div className="font-bold text-navy break-all">{profileUser.email}</div>
                  </div>
                  <div className="bg-surface rounded-lg p-3">
                    <div className="text-[10px] font-black uppercase text-[#94A3B8]">Téléphone</div>
                    <div className="font-bold text-navy">{profileUser.phone || "—"}</div>
                  </div>
                  <div className="bg-surface rounded-lg p-3">
                    <div className="text-[10px] font-black uppercase text-[#94A3B8]">Secteur</div>
                    <div className="font-bold text-navy">{profileUser.area || "—"}</div>
                  </div>
                  <div className="bg-surface rounded-lg p-3">
                    <div className="text-[10px] font-black uppercase text-[#94A3B8]">Code parrainage</div>
                    <div className="font-mono font-bold text-navy">{profileUser.referral_code || "—"}</div>
                  </div>
                  <div className="bg-surface rounded-lg p-3">
                    <div className="text-[10px] font-black uppercase text-[#94A3B8]">Inscrit le</div>
                    <div className="font-bold text-navy">{profileUser.created_at ? new Date(profileUser.created_at).toLocaleDateString("fr-CA") : "—"}</div>
                  </div>
                  {profileUser.role === "client" && (
                    <div className="bg-lime/10 rounded-lg p-3 border border-lime">
                      <div className="text-[10px] font-black uppercase text-[#94A3B8]">Crédits Go Helper</div>
                      <div className="font-black text-navy text-lg">{(profileUser.client_credits || 0).toFixed(2)} $</div>
                    </div>
                  )}
                  {profileUser.role === "helper" && profileUser.helper_profile && (
                    <>
                      <div className="bg-surface rounded-lg p-3">
                        <div className="text-[10px] font-black uppercase text-[#94A3B8]">Statut</div>
                        <div className="font-bold text-navy">
                          {profileUser.helper_profile.approved ? "✅ Approuvé" : "⏳ En attente"}
                        </div>
                      </div>
                      <div className="bg-surface rounded-lg p-3">
                        <div className="text-[10px] font-black uppercase text-[#94A3B8]">Tâches complétées</div>
                        <div className="font-bold text-navy">{profileUser.helper_profile.tasks_completed || 0}</div>
                      </div>
                      <div className="bg-surface rounded-lg p-3">
                        <div className="text-[10px] font-black uppercase text-[#94A3B8]">Note moyenne</div>
                        <div className="font-bold text-navy">⭐ {(profileUser.helper_profile.rating || 0).toFixed(1)}</div>
                      </div>
                      <div className="bg-lime/10 rounded-lg p-3 border border-lime">
                        <div className="text-[10px] font-black uppercase text-[#94A3B8]">Bonus parrainage</div>
                        <div className="font-black text-navy text-lg">{(profileUser.helper_profile.bonus_earned || 0).toFixed(2)} $</div>
                      </div>
                    </>
                  )}
                </div>

                {profileUser.role === "helper" && profileUser.helper_profile?.bio && (
                  <div className="bg-surface rounded-lg p-3">
                    <div className="text-[10px] font-black uppercase text-[#94A3B8]">Bio</div>
                    <p className="text-[#475569] mt-1">{profileUser.helper_profile.bio}</p>
                  </div>
                )}

                {profileApp && (
                  <div className="border-t pt-3">
                    <button onClick={() => { setProfileUser(null); setReviewApp(profileApp); }} className="btn-outline w-full text-sm" data-testid="profile-view-app">
                      Voir candidature complète →
                    </button>
                  </div>
                )}

                {profileUser.role === "helper" && (
                  <div className="border-t pt-3 space-y-2">
                    {!profileApp && !profileUser.helper_profile?.approved && (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
                        ⚠ Ce helper n'a pas soumis de candidature formelle (pas d'entrevue, pas de photos téléversées). Tu peux l'approuver directement si tu l'as déjà rencontré ou validé hors plateforme.
                      </div>
                    )}
                    {!profileUser.helper_profile?.approved ? (
                      <button
                        onClick={() => approveHelperDirect(profileUser)}
                        className="btn-primary w-full text-sm"
                        data-testid="profile-approve-helper"
                      >
                        ✅ Approuver ce helper directement
                      </button>
                    ) : (
                      <button
                        onClick={() => revokeHelper(profileUser)}
                        className="btn-outline w-full text-sm text-amber-700 border-amber-300"
                        data-testid="profile-revoke-helper"
                      >
                        ⏸ Retirer l'approbation
                      </button>
                    )}
                  </div>
                )}

                <div>
                  <h4 className="font-heading font-black text-navy">Tâches récentes ({profileTasks.length})</h4>
                  {profileTasks.length === 0 ? (
                    <p className="text-[#475569] text-sm mt-2">Aucune tâche.</p>
                  ) : (
                    <ul className="mt-2 space-y-1.5 max-h-60 overflow-y-auto">
                      {profileTasks.slice(0, 20).map((t) => (
                        <li key={t.id}>
                          <Link to={`/tasks/${t.id}`} onClick={() => setProfileUser(null)} className="block text-xs p-2 bg-surface hover:bg-lime/10 rounded-lg" data-testid={`profile-task-${t.id}`}>
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-bold text-navy truncate">{t.title}</span>
                              <span className="chip text-[9px]">{STATUS_LABELS[t.status] || t.status}</span>
                            </div>
                            <div className="text-[#475569] mt-0.5">{(t.total || 0).toFixed(2)} $ · {new Date(t.created_at).toLocaleDateString("fr-CA")}</div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={wipeOpen} onOpenChange={setWipeOpen}>
        <DialogContent data-testid="admin-wipe-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle size={20} /> Effacer la base de données
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <p className="text-[#475569]">Tu vas <strong className="text-red-700">effacer définitivement</strong> tous les éléments suivants :</p>
            <ul className="list-disc pl-5 space-y-0.5 text-[#475569]">
              <li>Toutes les tâches, soumissions, paiements</li>
              <li>Tous les messages, évaluations, notifications</li>
              <li>Toutes les candidatures helpers, parrainages</li>
              <li>Tous les utilisateurs (clients + helpers — selon le mode choisi)</li>
            </ul>
            <div className="space-y-2">
              <label className="text-sm font-bold text-navy">Choisis le mode :</label>
              <div className="grid gap-2">
                <button
                  type="button"
                  onClick={() => setWipeMode("safe")}
                  className={`text-left p-3 rounded-xl border-2 transition-all ${wipeMode === "safe" ? "border-navy bg-navy/5" : "border-[#E2E8F0]"}`}
                  data-testid="wipe-mode-safe"
                >
                  <div className="font-bold text-navy">🟢 Mode sécurisé (recommandé)</div>
                  <div className="text-xs text-[#475569] mt-0.5">Garde tous les admins. Efface clients, helpers et toutes leurs données.</div>
                </button>
                <button
                  type="button"
                  onClick={() => setWipeMode("full")}
                  className={`text-left p-3 rounded-xl border-2 transition-all ${wipeMode === "full" ? "border-red-500 bg-red-50" : "border-[#E2E8F0]"}`}
                  data-testid="wipe-mode-full"
                >
                  <div className="font-bold text-red-700">🔴 Mode total</div>
                  <div className="text-xs text-[#475569] mt-0.5">Efface absolument tout — sauf TON compte admin (pour ne pas te bloquer).</div>
                </button>
              </div>
            </div>
            <div>
              <label className="text-sm font-bold text-navy">
                Tape <span className="font-mono bg-surface px-1.5 py-0.5 rounded">EFFACER</span> pour confirmer
              </label>
              <input
                type="text"
                value={wipeConfirm}
                onChange={(e) => setWipeConfirm(e.target.value)}
                className="input-base mt-1"
                placeholder="EFFACER"
                autoComplete="off"
                data-testid="wipe-confirm-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <button onClick={() => setWipeOpen(false)} className="btn-outline" data-testid="cancel-wipe">Annuler</button>
            <button
              onClick={wipeAll}
              disabled={wiping || wipeConfirm.trim().toUpperCase() !== "EFFACER"}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-full px-5 py-2.5 inline-flex items-center gap-2"
              data-testid="confirm-wipe"
            >
              <Trash2 size={16} /> {wiping ? "Effacement…" : "Tout effacer maintenant"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ l, v }) {
  return (
    <div className="card-surface p-4">
      <div className="text-xs font-black uppercase tracking-wider text-[#94A3B8]">{l}</div>
      <div className="font-heading font-black text-3xl text-navy mt-1">{v}</div>
    </div>
  );
}

function AppRow({ l, v }) {
  return (
    <div className="flex gap-3">
      <div className="w-32 text-[#94A3B8] text-xs font-bold uppercase shrink-0">{l}</div>
      <div className="text-navy flex-1 break-words">{v || "—"}</div>
    </div>
  );
}

function PhotoPreview({ label, path }) {
  const token = typeof window !== "undefined" ? localStorage.getItem("gh_token") : null;
  const backend = process.env.REACT_APP_BACKEND_URL;
  return (
    <div className="bg-surface rounded-xl overflow-hidden">
      <div className="text-xs font-bold uppercase tracking-wider text-[#94A3B8] p-2">{label}</div>
      <img src={`${backend}/api/files/${path}?auth=${token}`} alt={label} className="w-full h-40 object-cover" />
    </div>
  );
}
